import { useTranslation } from 'react-i18next';
import { useNavigate, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useFocusedPatient } from '@/hooks/useFocusedPatient';
import { useSubscriptionRequired } from '@/hooks/useSubscriptionRequired';
import { useCompany } from '@/hooks/useCompany';
import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Heart, UserPlus, Users, Building2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PatientStatsCard } from '@/components/home/PatientStatsCard';
import { PullToRefresh } from '@/components/ui/pull-to-refresh';
import { OnboardingChecklist } from '@/components/onboarding/OnboardingChecklist';
import { useOnboardingProgress } from '@/hooks/useOnboardingProgress';
import { CoachMark } from '@/components/onboarding/CoachMark';
import { EmptyState } from '@/components/ui/empty-state';
import { PaymentIssueBanner } from '@/components/subscription/PaymentIssueBanner';

export default function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, roles } = useAuth();
  const { focusedPatient, isLoading: isFocusedLoading } = useFocusedPatient();
  const {
    isOwner,
    suspendedCount,
    isLoading: isLoadingSubscription,
    isRequired: isSubscriptionRequired,
  } = useSubscriptionRequired();
  const { isCompanyMember, isLoading: isCompanyLoading } = useCompany();
  const { subscription } = useAuth();

  const hasNoRole = roles.length === 0;

  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Lightweight count of active links to decide between EmptyState variants
  // and detect "fully new" users that should go through /app/welcome.
  const { data: linksData, isLoading: isLinksLoading, refetch: refetchLinks } = useQuery({
    queryKey: ['home-links-count', user?.id],
    queryFn: async () => {
      if (!user?.id) return { totalActive: 0, focusedRole: null as string | null };
      const { data } = await supabase
        .from('patient_user_links')
        .select('patient_id, role')
        .eq('user_id', user.id)
        .eq('active', true);
      const links = data ?? [];
      const focusedLink = focusedPatient
        ? links.find((l) => l.patient_id === focusedPatient.id)
        : null;
      return {
        totalActive: links.length,
        focusedRole: focusedLink?.role ?? null,
      };
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000,
  });

  const totalActive = linksData?.totalActive ?? 0;
  const focusedRole = linksData?.focusedRole ?? null;
  const displayName = profile?.full_name || user?.email;

  const { state: onboardingState } = useOnboardingProgress();
  const hasRealUsage = !!onboardingState?.hasRealUsage;

  const isFullyLoaded =
    !isLinksLoading && !isCompanyLoading && !isFocusedLoading && !isLoadingSubscription;

  // Redirect new users to welcome/persona selector
  if (isFullyLoaded && hasNoRole && !isCompanyMember && totalActive === 0) {
    return <Navigate to="/app/welcome" replace />;
  }

  const showUpgradeCta =
    isOwner && !subscription.subscribed && (isSubscriptionRequired || suspendedCount > 0);
  const showCaregiverPromo = !isOwner && totalActive > 0;
  const showCreateCompanyCta = !isOwner && !isCompanyMember && totalActive > 0;

  return (
    <AppLayout showFAB={false} title="Care - OS">
      <PullToRefresh onRefresh={async () => { await refetchLinks(); }} className="min-h-full">
        <div className="p-4 max-w-4xl mx-auto space-y-6">
          {/* Greeting */}
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {t('auth.welcomeBack')}
            </h1>
            <p className="text-muted-foreground text-sm">{displayName}</p>
          </div>

          {/* Payment grace / expired banner */}
          <PaymentIssueBanner />

          {/* Suspended patients alert */}
          {suspendedCount > 0 && (
            <div
              className="rounded-lg border border-warning/40 bg-warning/10 p-4 flex items-center gap-3 cursor-pointer"
              onClick={() => navigate('/app/patients')}
            >
              <div className="flex-1 text-sm">
                <strong className="text-warning">
                  {t('patient.suspended.dashboardTitle', {
                    count: suspendedCount,
                    defaultValue: '{{count}} paciente(s) suspenso(s) por limite do plano',
                  })}
                </strong>
                <p className="text-muted-foreground mt-1">
                  {t('patient.suspended.dashboardSub', {
                    defaultValue: 'Adicione mais slots ou arquive outro paciente para reativá-los.',
                  })}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); navigate('/app/subscription'); }}
                className="shrink-0"
              >
                {t('patient.suspended.manage', { defaultValue: 'Gerenciar' })}
              </Button>
            </div>
          )}

          {/* Onboarding checklist (renders only if incomplete) */}
          <div data-tour="onboarding-checklist">
            <OnboardingChecklist />
          </div>

          {/* Focused patient */}
          <section data-tour="patients-section" className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                {t('home.focused.title')}
              </h2>
              {focusedPatient && (
                <Button variant="ghost" size="sm" onClick={() => navigate('/app/patients')}>
                  {t('home.focused.changeCta')}
                </Button>
              )}
            </div>

            {(isLinksLoading || isFocusedLoading) ? (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4 mb-4">
                    <Skeleton className="w-16 h-16 rounded-full" />
                    <div className="flex-1">
                      <Skeleton className="h-5 w-32 mb-2" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {[1, 2, 3].map((j) => (
                      <Skeleton key={j} className="h-16 rounded-lg" />
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : focusedPatient ? (
              <PatientStatsCard
                patient={{ ...focusedPatient, role: focusedRole || 'observer' }}
              />
            ) : totalActive > 0 ? (
              <EmptyState
                icon={Users}
                title={t('home.focused.emptyTitle')}
                description={t('home.focused.emptyDescription')}
                action={
                  <Button onClick={() => navigate('/app/patients')}>
                    {t('home.focused.emptyCta')}
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={Heart}
                title={t('patient.noPatients')}
                description={t('patient.noPatientsDescription')}
                action={
                  <Button onClick={() => navigate('/app/patients/new')}>
                    {t('home.focused.firstPatientCta')}
                  </Button>
                }
              />
            )}
          </section>

          {/* Conversion CTAs */}
          {isFullyLoaded && (showUpgradeCta || showCaregiverPromo || showCreateCompanyCta) && (
            <section className="space-y-3">
              {showUpgradeCta && (
                <Card className="border-primary/30 bg-primary/5">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm mb-1">{t('home.cta.upgradeTitle')}</h3>
                        <p className="text-xs text-muted-foreground mb-3">
                          {t('home.cta.upgradeDescription')}
                        </p>
                        <Button size="sm" onClick={() => navigate('/app/subscription')}>
                          {t('home.cta.upgradeAction')}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {showCaregiverPromo && (
                <Card className="border-primary/30 bg-primary/5">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <Heart className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm mb-1">
                          {t('subscription.caregiverPromo.title')}
                        </h3>
                        <p className="text-xs text-muted-foreground mb-3">
                          {t('subscription.caregiverPromo.description')}
                        </p>
                        <Button size="sm" variant="outline" onClick={() => navigate('/app/patients/new')}>
                          <UserPlus className="h-4 w-4 mr-1" />
                          {t('subscription.caregiverPromo.cta')}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {showCreateCompanyCta && (
                <Card className="border-primary/30 bg-primary/5">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm mb-1">
                          {t('home.cta.createCompanyTitle')}
                        </h3>
                        <p className="text-xs text-muted-foreground mb-3">
                          {t('home.cta.createCompanyDescription')}
                        </p>
                        <Button size="sm" variant="outline" onClick={() => navigate('/app/company/new')}>
                          {t('home.cta.createCompanyAction')}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </section>
          )}
        </div>
      </PullToRefresh>

      {/* Guided tour for new users */}
      {isFullyLoaded && !hasRealUsage && (
        <CoachMark
          tourId="home"
          steps={[
            {
              target: 'onboarding-checklist',
              titleKey: 'tour.home.checklist.title',
              descKey: 'tour.home.checklist.desc',
            },
            {
              target: 'patients-section',
              titleKey: 'tour.home.patients.title',
              descKey: 'tour.home.patients.desc',
            },
            {
              target: 'app-navigation',
              titleKey: 'tour.home.navigation.title',
              descKey: 'tour.home.navigation.desc',
            },
          ]}
        />
      )}
    </AppLayout>
  );
}
